// INSERT-target slot detector (Workstream C, mssql 45.§2/§9).
//
// The clause machine maps everything from the INSERT keyword to VALUES into
// `insert-columns` → CPK `'values'`, so the resolvers' values arm treats the
// caret right after `INSERT [INTO]` as a column slot and emits bound-alias
// columns — noise, because the user is naming a RELATION there (and #temp /
// @table-variable synthetics were never emitted at all). This text-walk
// recognises the target sub-slot the same way `isAtJoinTargetSlot` does (and
// the retired string-walk `classifyDdlPositionKind` did): by the pre-caret
// statement tail.
//
// Matches when the caret sits after `INSERT` (optionally `INTO`) with at most
// a partial identifier still being typed — `INSERT INTO ▮`, `INSERT ▮`,
// `INSERT INTO #tm▮`. Does NOT match once the target is complete and a new
// token has started: `INSERT INTO t (▮` (column list), `… VALUES (▮`,
// `INSERT INTO t ▮` (next keyword slot).

import { stripCommentsPreserving } from './sqlClauseToPositionKind';

export interface InsertTargetSlot {
  /** True when INTO hasn't been typed yet (`INSERT ▮`) — the resolver should
   *  offer the INTO keyword alongside relations. */
  readonly needsInto: boolean;
}

// Tail = INSERT, optional engine-specific INSERT modifiers (sqlite
// `OR REPLACE|IGNORE|ROLLBACK|ABORT|FAIL`, mysql `LOW_PRIORITY|HIGH_PRIORITY|
// DELAYED|IGNORE`), optional INTO, then only an optional in-progress identifier.
// Relation chars cover T-SQL #temp/@var prefixes + [bracketed], "quoted",
// `backtick`-quoted (mysql/sqlite) idents, and dots. The modifier/backtick
// widening is a strict superset, so the mssql/oracle callers are unaffected
// (those dialects never produce the new forms). `m[1]` is still the INTO group.
const INSERT_TARGET_TAIL_RE =
  /\bINSERT\s+(?:(?:OR\s+(?:REPLACE|IGNORE|ROLLBACK|ABORT|FAIL)|LOW_PRIORITY|HIGH_PRIORITY|DELAYED|IGNORE)\s+)*(INTO\s+)?(?:[#@]{0,2}[A-Za-z_["`][\w.$#[\]"`]*)?$/i;

export function isAtInsertTargetSlot(
  text: string,
  offset: number,
  statementStart: number,
): InsertTargetSlot | null {
  const slice = stripCommentsPreserving(text.slice(statementStart, offset));
  const m = INSERT_TARGET_TAIL_RE.exec(slice);
  if (!m) return null;
  return { needsInto: m[1] === undefined };
}
